import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { Info } from "lucide-react";
import { Fragment, useState } from "react";

import { Sparkline } from "./Sparkline";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import type { PgFicoGridRow, RuleDefinition, RuleMetric } from "../lib/api/types";
import { cn } from "../lib/utils";

export interface RuleRow {
  definition: RuleDefinition;
  metric: RuleMetric;
}

function CandidateConfirmedBar({
  candidateCount,
  confirmedCount,
}: {
  candidateCount: number;
  confirmedCount: number;
}) {
  const total = Math.max(1, candidateCount + confirmedCount);
  return (
    <div className="ml-auto w-[140px] space-y-1.5">
      <div className="flex h-2 w-full overflow-hidden bg-secondary">
        <div
          className="h-full bg-neutral-inactive"
          style={{ width: `${(candidateCount / total) * 100}%` }}
        />
        <div className="h-full bg-primary" style={{ width: `${(confirmedCount / total) * 100}%` }} />
      </div>
      <p className="text-right text-[11px] tabular-nums text-muted-foreground">
        {candidateCount} cand · {confirmedCount} conf
      </p>
    </div>
  );
}

function PgFicoGridDialog({ grid }: { grid: PgFicoGridRow[] }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-6 text-muted-foreground hover:text-primary"
          aria-label="Open the PG FICO grid reference"
        >
          <Info className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>PG FICO Grid — reference</DialogTitle>
          <DialogDescription>
            B1a and B1b are both evaluated against this grid.
          </DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Risk grade</TableHead>
              <TableHead className="text-right text-xs">&lt; 2 yrs</TableHead>
              <TableHead className="text-right text-xs">2–4 yrs</TableHead>
              <TableHead className="text-right text-xs">4–5 yrs</TableHead>
              <TableHead className="text-right text-xs">&gt; 5 yrs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grid.map((row) => (
              <TableRow key={row.riskGrade}>
                <TableCell className="text-sm font-medium">{row.riskGrade}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{row.under2Years}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {row.twoToFourYears}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {row.fourToFiveYears}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {row.overFiveYears}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Marks a rule whose count this database cannot fully measure.
 *
 * WHY THIS IS NOT OPTIONAL. Nine of the thirteen rules read fully-populated inputs. Four
 * do not: B1a and B1b are undeterminable for the ~37% of applications with no
 * time-in-business, B2 is missing its PayNet leg entirely, and A2 has no crane-exemption
 * list. Their counts are floors. Rendered without this badge, a floor sits in the same
 * column as a complete count and is read as a total — and a reader concluding "only 513
 * PG deals breached the FICO floor" when the real answer is "at least 513, and we cannot
 * see 37% of the book" is worse off than one who saw no number at all.
 *
 * `detectionNote` carries the specific reason per rule and goes in the tooltip.
 */
function DetectionStatusBadge({ definition }: { definition: RuleDefinition }) {
  if (definition.detectionStatus === "implemented") return null;

  const label =
    definition.detectionStatus === "partial_coverage"
      ? "Partial coverage"
      : definition.detectionStatus === "definition_disputed"
        ? "Definition disputed"
        : "Not measured";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="cursor-help border-muted-foreground/40 text-[10px] uppercase tracking-wide text-muted-foreground"
        >
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">
        <p className="text-[11px] leading-relaxed">
          {definition.detectionNote ??
            "This rule's inputs are incomplete, so its count is a lower bound."}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

const columnHelper = createColumnHelper<RuleRow>();

function makeColumns(grid: PgFicoGridRow[]): ColumnDef<RuleRow, any>[] {
  return [
    columnHelper.display({
      id: "rule",
      header: "Rule",
      cell: ({ row }) => {
        const { definition } = row.original;
        return (
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">
                  <span className="mr-2 bg-secondary px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
                    {definition.ruleId}
                  </span>
                  <span className="font-medium text-foreground underline decoration-dotted decoration-muted-foreground/60 underline-offset-4">
                    {definition.label}
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-mono text-[11px] leading-relaxed">
                  {definition.detectionLogic}
                </p>
              </TooltipContent>
            </Tooltip>
            {definition.ruleId === "B1a" ? <PgFicoGridDialog grid={grid} /> : null}
            {definition.isReviewFlag ? (
              <Badge
                variant="outline"
                className="border-primary/50 bg-amber-dim text-[10px] uppercase tracking-wide text-primary"
              >
                Review flag
              </Badge>
            ) : null}
            <DetectionStatusBadge definition={definition} />
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "count",
      header: "Count",
      cell: ({ row }) => {
        const { definition, metric } = row.original;
        const partial = definition.detectionStatus !== "implemented";
        return (
          <span className="font-semibold text-foreground">
            {metric.count.toLocaleString()}
            {/* A partial rule's count is a FLOOR. Without this marker it sits in the same
                column as a complete count and reads as the total — which is the single most
                likely way this page misleads someone. */}
            {partial ? (
              <span className="ml-1 align-super text-[10px] font-normal text-muted-foreground">
                +
              </span>
            ) : null}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "pct",
      header: "% of approved",
      cell: ({ row }) => `${row.original.metric.pctOfApprovedPopulation.toFixed(2)}%`,
    }),
    columnHelper.display({
      id: "reconciliation",
      header: "Candidate / confirmed",
      cell: ({ row }) => (
        <CandidateConfirmedBar
          candidateCount={row.original.metric.candidateCount}
          confirmedCount={row.original.metric.confirmedCount}
        />
      ),
    }),
    columnHelper.display({
      id: "trend",
      header: "Trend",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Sparkline data={row.original.metric.sparkline} width={110} />
        </div>
      ),
    }),
  ];
}

export function RuleTable({ rows, grid }: { rows: RuleRow[]; grid: PgFicoGridRow[] }) {
  const [columns] = useState(() => makeColumns(grid));

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TooltipProvider delayDuration={120}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn("text-xs", header.id !== "rule" && "text-right")}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const { definition } = row.original;
              const note = definition.specialNote;
              return (
                <Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      note && "border-b-0",
                      definition.isReviewFlag && "bg-amber-dim/40",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "text-sm",
                          cell.column.id !== "rule" && "text-right tabular-nums",
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {note ? (
                    <TableRow
                      className={cn(definition.isReviewFlag && "bg-amber-dim/40")}
                    >
                      <TableCell colSpan={row.getVisibleCells().length} className="pt-0">
                        <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground">
                          {note}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
